import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Paper,
  Fab,
  Icon,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Autocomplete,
  TableContainer,
  TableCell,
  TableHead,
  TableBody,
  Table,
  TableRow,
  Divider,
  Tooltip,
  Checkbox,
} from "@mui/material";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import AddEmployeeDialog from "components/AddEmployeeDialog";
import MDBox from "components/MDBox";
import { addEmployee } from "services/api";
import EmployeeModel from "models/employeeModel";
import { deleteEmployee } from "services/api";
import { useMaterialUIController } from "context";
import MDTypography from "components/MDTypography";
import { updateEmployee } from "services/api";
import { useAuth } from "context/authContext";
import EmployeeAvatar from "components/EmployeeAvatar";
import { uploadPhoto } from "services/api";
import EmployeeCard from "components/EmployeeCard";
import { FixedSizeList } from "react-window";
import { getEmployeesPaging } from "services/api";
import { getEmployees } from "services/api";
import {
  AccountTree,
  CloudDownload,
  CloudUpload,
  TableChart,
  TableRows,
} from "@mui/icons-material";
import { exportData } from "services/api";
import * as XLSX from "xlsx";
import { detectDataType } from "services/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TruncatedCell from "./components/TruncatedCell";
import { ReactFlowProvider } from "reactflow";
import EmployeeOrgChart from "./components/Nodes";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import LoadingScreen from "components/Loading";
import ConfirmationDialog from "components/ConfirmationDialog";
import AccessRightsDialog from "components/AccessRightsDialog";
import { useAppTheme } from "context/themeContext";
import ImportButton from "components/ImportButton";

const PAGE_SIZE = 20;

const EmployeesView = ({ jobs, branches, departments, canEdit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { employee, companies, allEmps, setAllEmps } = useAuth();
  const { primaryColor } = useAppTheme();
  const [view, setView] = useState(0);
  //employees paging
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingNew, setLoadingNew] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const snackbarRef = useRef();
  const [filterNoJob, setFilterNoJob] = useState(false);
  const [filterNoBranch, setFilterNoBranch] = useState(false);
  const [filterNoReference, setFilterNoReference] = useState(false);
  const [filterNoDirectManager, setFilterNoDirectManager] = useState(false);

  const [added, setAdded] = useState(false);
  //Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [toDeleteEmployees, setToDeleteEmployees] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [confOpen, setConfOpen] = useState(false);

  const [accessOpen, setAccessOpen] = useState(false);
  const [employeeAccessRightsId, setEmployeeAccessRightsId] = useState();
  const [employeeAccessRightsName, setEmployeeAccessRightsName] = useState("");

  // 1. OPTIMIZED: Fetch Data Only
  useEffect(() => {
    const fetchInitialEmployees = async () => {
      // If we already have data in context, don't re-fetch
      if (allEmps.length > 0) return;

      setLoading(true);
      const data = await getEmployees();
      console.log("Fetched employees:", data); // Debugging line
      if (data !== "error") {
        // Just set the master list. The useMemo below will handle the rest.
        setAllEmps(data);
      }
      setLoading(false);
    };

    fetchInitialEmployees();
  }, []); // Empty dependency array = runs once on mount

  // 2. OPTIMIZED: Memoize the Filtered List
  // This only recalculates when filters or allEmps change, not on every render
  const filteredEmployees = useMemo(() => {
    return allEmps.filter(
      (emp) =>
        (selectedJob ? emp.jobId === selectedJob : true) &&
        (selectedBranch ? emp.branchId === selectedBranch : true) &&
        (selectedRole !== undefined ? emp.isAdmin === selectedRole : true) &&
        (search ? emp.name?.toLowerCase().includes(search.toLowerCase()) : true) &&
        (!filterNoJob || !emp.jobId) &&
        (!filterNoBranch || !emp.branchId) &&
        (!filterNoReference || !emp.referencedEmployeeId) &&
        (!filterNoDirectManager || !emp.directManagerId)
    );
  }, [
    allEmps,
    selectedJob,
    selectedBranch,
    selectedRole,
    search,
    filterNoJob,
    filterNoBranch,
    filterNoReference,
    filterNoDirectManager,
  ]);

  // 3. OPTIMIZED: Calculate Stats in ONE pass (instead of 4 filters)
  const stats = useMemo(() => {
    // We calculate stats based on the FILTERED list (or use allEmps if you want global stats)
    const dataset = filteredEmployees;

    return dataset.reduce(
      (acc, emp) => {
        if (!emp.jobId) acc.noJobCount++;
        if (!emp.branchId) acc.noBranchCount++;
        if (!emp.referencedEmployeeId) acc.noReferenceCount++; // Ensure property name matches DB
        if (!emp.directManagerId) acc.noDirectManagerCount++;
        return acc;
      },
      {
        employeeCount: dataset.length,
        noJobCount: 0,
        noBranchCount: 0,
        noReferenceCount: 0,
        noDirectManagerCount: 0,
      }
    );
  }, [filteredEmployees]);

  // 4. OPTIMIZED: Update View List when Filters Change
  // When the filtered list changes, reset the visible "employees" to page 1
  useEffect(() => {
    setEmployees(filteredEmployees.slice(0, PAGE_SIZE));
    setPage(1);
  }, [filteredEmployees]);

  // 5. Scroll Loader (Kept mostly same, just simplified)
  const loadMoreEmployees = () => {
    if (loading || employees.length >= filteredEmployees.length) return;

    const nextBatch = filteredEmployees.slice(employees.length, employees.length + PAGE_SIZE);

    if (nextBatch.length > 0) {
      setEmployees((prev) => [...prev, ...nextBatch]);
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100) {
        loadMoreEmployees();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [employees, filteredEmployees, loading]);

  // Handle Snackbar close

  const buttonStyles = (filterType, isSelected) => {
    const colors = {
      noJob: "#e67e22", // Orange
      active: "#27ae60", // Green
      inactive: "#7f8c8d", // Gray
      expired: "#c0392b", // Red
      all: "#2980b9", // Blue
    };

    const bgColor = isSelected ? colors[filterType] : "transparent";
    const textColor = isSelected ? "#fff" : colors[filterType];

    return {
      borderColor: colors[filterType],
      color: textColor,
      backgroundColor: bgColor,
      "&:hover": {
        backgroundColor: colors[filterType],
        color: "#fff",
        borderColor: colors[filterType],
      },
      "&.MuiButton-contained": { backgroundColor: colors[filterType] },
      "&:focus, &:focus-visible, &:focus-within": {
        backgroundColor: `${colors[filterType]} !important`,
        borderColor: `${colors[filterType]} !important`,
        color: "#fff !important",
        outline: "none",
        boxShadow: "none",
      },
    };
  };

  const handleOpenDialog = (employee) => {
    if (!canEdit) return;
    setSelectedEmployee(employee);
    setOpenDialog(true);
  };

  const handleSelectEmployee = (id) => {
    setToDeleteEmployees((prev) =>
      prev.includes(id) ? prev.filter((empId) => empId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelectedEmployees = async () => {
    if (toDeleteEmployees.length === 0) return;
    try {
      const results = await Promise.all(
        toDeleteEmployees.map(async (id) => {
          if (employee.id == id) {
            snackbarRef.current?.showSnackbar(t("admin.employees.cannotDeleteSelf"), "error");
            return Promise.resolve("error");
          }

          const res = await deleteEmployee(id);

          if (res === "success") {
            // return "success";
            return { id, status: "success" };
          } else if (res[0] === "C") {
            snackbarRef.current?.showSnackbar("error1", "error");
            return { id, status: "assigned" };
          } else if (res[0] === "U") {
            snackbarRef.current?.showSnackbar("error2", "error");
            return { id, status: "error" };
          }
        })
      );

      // Only remove successfully deleted employees from UI
      const deletedIds = results.filter((r) => r.status === "success").map((r) => r.id);

      if (deletedIds.length > 0) {
        setEmployees((prev) => prev.filter((emp) => !deletedIds.includes(emp.id)));
        setAllEmps((prev) => prev.filter((emp) => !deletedIds.includes(emp.id)));
        snackbarRef.current?.showSnackbar(
          t("admin.employees.selectedEmployeesDeletedSuccess"),
          "success"
        );
      }

      setToDeleteEmployees([]);
      setDeleteMode(false);
    } catch (err) {
      snackbarRef.current?.showSnackbar(
        t("admin.employees.selectedEmployeesDeletedError"),
        "error"
      );
    }
  };

  const roles = {
    Admin: true,
    User: false,
  };

  const handleSave = async (newEmployee, departmentId) => {
    const normalizedEmployeeName = newEmployee.name.trim().toLowerCase();

    // Check if the name already exists, excluding the current employee if updating
    const nameExists = employees.some(
      (e) =>
        e.name.trim().toLowerCase() === normalizedEmployeeName &&
        (!selectedEmployee || e.id !== selectedEmployee.id) // Exclude the employee being updated
    );

    if (nameExists) {
      snackbarRef.current?.showSnackbar(t("admin.employees.employeeNameExists"), "error");
      return;
    }
    if (selectedEmployee) {
      selectedEmployee.branchId = newEmployee.branch;
      selectedEmployee.directManagerId = newEmployee.directManager;
      selectedEmployee.dob = newEmployee.dob;
      selectedEmployee.email = newEmployee.email;
      selectedEmployee.gender = newEmployee.gender;
      selectedEmployee.name = newEmployee.name;
      selectedEmployee.jobId = newEmployee.job;
      selectedEmployee.isAdmin = newEmployee.isAdmin;
      selectedEmployee.referencedEmployeeId = newEmployee.referencedEmployeeId;
      selectedEmployee.phoneNumber = newEmployee.phoneNumber;
      selectedEmployee.photo = newEmployee.photo;
      selectedEmployee.indirectManagerId = newEmployee.indirectManager;
      selectedEmployee.departmentId = departmentId;
      selectedEmployee.jobGroupId = newEmployee.jobGroup;
      selectedEmployee.employmentType = newEmployee.employmentType;
      updateEmployee(selectedEmployee).then((emp) => {
        if (emp !== "error") {
          const empData = new EmployeeModel(selectedEmployee);
          setEmployees((prevEmployees) =>
            prevEmployees.map((e) => (e.id === empData.id ? empData : e))
          );
          setAllEmps((emps) => emps.map((e) => (e.id === empData.id ? empData : e)));
          snackbarRef.current?.showSnackbar(t("admin.employees.employeeUpdateSuccess"), "success");
        } else {
          snackbarRef.current?.showSnackbar(t("admin.employees.employeeUpdateSuccess"), "error");
        }
        setSnackbarOpen(true);
      });
      setSelectedEmployee(null);
    } else {
      const newEmp = new EmployeeModel({
        name: newEmployee.name,
        email: newEmployee.email,
        phoneNumber: newEmployee.phoneNumber,
        role: 2,
        isAdmin: newEmployee.isAdmin,
        jobId: newEmployee.job,
        gender: newEmployee.gender,
        dob: newEmployee.dob,
        directManagerId: newEmployee.directManager,
        branchId: newEmployee.branch,
        departmentId: departmentId,
        referencedEmployeeId: newEmployee.referencedEmployeeId,
        indirectManagerId: newEmployee.indirectManager,
        companyId: companies[0].companyId,
        language: companies[0].language,
        jobGroupId: newEmployee.jobGroup,
        employmentType: newEmployee.employmentType,
      });
      addEmployee(newEmp).then(async (emp) => {
        if (emp !== "error") {
          const empData = emp;
          const sorted = [...allEmps, empData].sort((a, b) => a.jobId - b.jobId);
          setAllEmps(sorted);
          setAdded(!added);
          snackbarRef.current?.showSnackbar(t("admin.employees.employeeAddSuccess"), "success");
        } else {
          snackbarRef.current?.showSnackbar(t("admin.employees.employeeAddError"), "error");
        }
        setSnackbarOpen(true);
      });
    }
  };

  const handleDelete = async (id) => {
    if (employee.id == id) {
      snackbarRef.current?.showSnackbar(t("admin.employees.cannotDeleteSelf"), "error");
      return;
    }
    deleteEmployee(id).then(async (response) => {
      if (response === "success") {
        snackbarRef.current?.showSnackbar(t("admin.employees.employeeDeletedSuccess"), "success");

        setEmployees((prevEmployees) => prevEmployees.filter((employee) => employee.id !== id));
        setAllEmps((prevEmps) => prevEmps.filter((employee) => employee.id !== id));
      } else if (response[0] === "C") {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.cannotDeleteWithCompetencies"),
          "error"
        );
      } else if (response[0] === "U") {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.cannotDeleteWithSuccessionPlan"),
          "error"
        );
      } else {
        snackbarRef.current?.showSnackbar(t("admin.employees.employeeAddError"), "error");
      }
      setSnackbarOpen(true);
    });
  };

  //IMPORT AND EXPORT
  const handleExport = async () => {
    setDownloadLoading(true);

    await exportData("employees");
    setDownloadLoading(false);
  };

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  if (loadingDelete) {
    return <LoadingScreen />;
  }

  if (view === 1) {
    return (
      <ReactFlowProvider>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
            <MDButton
              color="primary"
              onClick={handleExport}
              startIcon={<CloudDownload />}
              variant="contained"
            >
              {downloadLoading ? <CircularProgress size={20} color="inherit" /> : t("export")}
            </MDButton>

            <ImportButton
              // Only allow Jobs to be imported from this page
              allowedTypes={["employees"]}
              // Pass context needed for the ImportReview page (optional now, mostly companyId)
              extraState={{
                employees: allEmps,
                jobs,
                empBranches: branches,
                departments,
                companyId: companies[0].companyId,
              }}
              // Handle UI feedback
              onError={handleImportError}
              disabled={!canEdit}
            />

            <MDButton
              color="primary"
              variant="contained"
              onClick={() => setView(view === 0 ? 1 : 0)}
            >
              <TableRows />
            </MDButton>
          </Stack>
        </Box>

        <EmployeeOrgChart allEmps={allEmps} jobs={jobs} setAllEmps={setAllEmps} canEdit={canEdit} />
      </ReactFlowProvider>
    );
  }

  return (
    <Box p={3}>
      {/* Filter Section */}
      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap" mb={3}>
        {/* Job Filter */}
        <Autocomplete
          options={jobs || []}
          getOptionLabel={(option) => option.jobName || ""}
          value={jobs?.find((j) => j.jobId === selectedJob) || null}
          onChange={(e, newValue) => setSelectedJob(newValue?.jobId || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.employees.filters.job")}
              variant="outlined"
              size="medium"
            />
          )}
          sx={{ minWidth: 200 }}
          isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
        />
        <Autocomplete
          options={branches || []}
          getOptionLabel={(option) => option.branchName || ""}
          value={branches.find((br) => br.branchId === selectedBranch) || null}
          onChange={(e, newValue) => setSelectedBranch(newValue?.branchId || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.employees.filters.branch")}
              variant="outlined"
              size="medium"
            />
          )}
          sx={{ minWidth: 200 }}
          isOptionEqualToValue={(option, value) => option.branchId === value.branchId}
        />
        <FormControl sx={{ minWidth: 200, height: "56px" }}>
          <InputLabel shrink={true}>{t("admin.employees.filters.role")}</InputLabel>
          <Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            displayEmpty
            sx={{ height: "56px", fontSize: "1rem", paddingY: "10px" }}
          >
            <MenuItem value="">{t("admin.employees.filters.allRoles")}</MenuItem>
            {Object.entries(roles).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={t("admin.employees.filters.search")}
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            minWidth: 200,
            height: "56px",
            "& .MuiInputBase-root": { height: "56px", fontSize: "1rem", paddingY: "10px" },
          }}
        />
        {/* Missing Job Filter */}
        <Button
          variant={filterNoJob ? "contained" : "outlined"}
          color="error"
          sx={buttonStyles("noJob", filterNoJob)}
          onClick={() => setFilterNoJob(!filterNoJob)}
        >
          {t("admin.employees.filters.noJob", { count: stats?.noJobCount || 0 })}
        </Button>
        {/* Missing Branch Filter */}
        <Button
          variant={filterNoBranch ? "contained" : "outlined"}
          color="warning"
          sx={buttonStyles("noJob", filterNoBranch)}
          onClick={() => setFilterNoBranch(!filterNoBranch)}
        >
          {t("admin.employees.filters.noBranch", { count: stats?.noBranchCount || 0 })}
        </Button>
        {/* Missing Referenced Employee Filter */}
        <Button
          variant={filterNoReference ? "contained" : "outlined"}
          color="primary"
          sx={buttonStyles("noJob", filterNoReference)}
          onClick={() => setFilterNoReference(!filterNoReference)}
        >
          {t("admin.employees.filters.noId", { count: stats?.noReferenceCount || 0 })}
        </Button>
        {/* Missing Referenced Employee Filter */}
        <Button
          variant={filterNoDirectManager ? "contained" : "outlined"}
          color="primary"
          sx={buttonStyles("noJob", filterNoDirectManager)}
          onClick={() => setFilterNoDirectManager(!filterNoDirectManager)}
        >
          {t("admin.employees.filters.noManager", { count: stats?.noDirectManagerCount || 0 })}
        </Button>
        {/* Clear Filters Button */}
        <Button
          color="primary"
          onClick={() => {
            setSelectedJob("");
            setSelectedBranch("");
            setSearch("");
            setSelectedRole("");
            setFilterNoJob(false);
            setFilterNoBranch(false);
            setFilterNoReference(false);
          }}
        >
          {t("admin.employees.filters.clearFilters")}
        </Button>

        <Stack direction="row" spacing={2}>
          <MDButton
            variant="outlined"
            color={deleteMode ? "secondary" : "error"}
            disabled={!canEdit}
            onClick={() => setDeleteMode(!deleteMode)}
          >
            {deleteMode
              ? t("admin.employees.delete.cancel")
              : t("admin.employees.delete.deleteMultiple")}
          </MDButton>

          {deleteMode && toDeleteEmployees.length > 0 && (
            <MDButton
              color="error"
              onClick={() => setConfOpen(true)} // open confirmation instead
            >
              {t("admin.employees.delete.deleteSelected")} ({toDeleteEmployees.length})
            </MDButton>
          )}
        </Stack>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <MDTypography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {t("admin.employees.stats.totalEmployees", { count: stats?.employeeCount || 0 })}
        </MDTypography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
          <MDButton color="primary" onClick={handleExport} startIcon={<CloudDownload />}>
            {downloadLoading ? <CircularProgress size={20} color="inherit" /> : t("export")}
          </MDButton>
          <ImportButton
            // Only allow Jobs to be imported from this page
            allowedTypes={["employees"]}
            // Pass context needed for the ImportReview page (optional now, mostly companyId)
            extraState={{
              employees: allEmps,
              jobs,
              empBranches: branches,
              departments,
              companyId: companies[0].companyId,
            }}
            // Handle UI feedback
            onError={handleImportError}
            disabled={!canEdit}
          />
          <MDButton
            color="primary"
            onClick={() => {
              setView(view == 0 ? 1 : 0);
            }}
          >
            <AccountTree />
          </MDButton>
        </Stack>
      </Box>
      {/* Employees Grouped by Job */}

      {!loadingNew && (
        <Box display="flex" flexDirection="column" gap={5}>
          {jobs?.map((job) => {
            const jobEmployees = employees.filter((emp) => emp.jobId === job.jobId);
            if (jobEmployees.length === 0) return null;
            return (
              <Box key={job.jobId}>
                {/* Job Title Header */}
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    backgroundColor: primaryColor,
                    color: "#fff",
                    padding: { xs: "4px 8px", sm: "6px 12px" },
                    borderRadius: "8px",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                    display: "inline-block",
                    mb: 2,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  {job.jobName}
                </Typography>

                {/* Table for this job */}
                <TableContainer
                  component={Paper}
                  sx={{
                    backgroundColor: darkMode ? "#333" : "#fff",
                    border: darkMode ? "2px solid " + primaryColor : null,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    borderRadius: 2,
                    mb: 4,
                  }}
                >
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>{t("user.myTeam.photo")}</TableCell>

                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.name")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.email")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.branch")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.role")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.employmentType")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.directManager")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.employees.employeeDialog.fields.accessRights")}
                        </TableCell>
                      </TableRow>
                      {jobEmployees.map((employee) => (
                        <TableRow
                          key={employee.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                          onClick={() => !deleteMode && handleOpenDialog(employee)}
                        >
                          <TableCell>
                            <EmployeeAvatar photo={employee.photo} />
                          </TableCell>

                          <TruncatedCell value={employee.name} eId={employee.eid} />
                          <TruncatedCell value={employee.email} />
                          <TruncatedCell
                            value={
                              branches.find((b) => b.branchId === employee.branchId)?.branchName ||
                              "-"
                            }
                          />

                          {/* Role from isAdmin */}
                          <TruncatedCell value={employee.isAdmin ? "Admin" : "User"} />

                          {/* Employment Type Formatting */}
                          <TruncatedCell
                            value={
                              employee.employmentType === "FULL_TIME"
                                ? "Full Time"
                                : employee.employmentType === "PART_TIME"
                                  ? "Part Time"
                                  : "-"
                            }
                          />

                          {/* Manager Name from managerId */}
                          <TruncatedCell
                            value={
                              allEmps?.find((e) => e.id === employee.directManagerId)?.name || "-"
                            }
                          />

                          {deleteMode && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={toDeleteEmployees.includes(employee.id)}
                                onChange={() => handleSelectEmployee(employee.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            {employee.isAdmin && (
                              <Button
                                size="small"
                                disabled={!canEdit}
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent triggering row click

                                  setEmployeeAccessRightsId(employee.id);
                                  setEmployeeAccessRightsName(employee.name);
                                  setAccessOpen(true);
                                }}
                              >
                                {t("admin.employees.employeeDialog.fields.accessRights")}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Divider after each job group */}
              </Box>
            );
          })}

          {/* Employees Without Job */}
          {filterNoJob &&
            (() => {
              const jobIds = new Set(jobs?.map((j) => j.jobId));
              const employeesWithoutJob = employees.filter(
                (emp) => !emp.jobId || !jobIds.has(emp.jobId)
              );

              if (employeesWithoutJob.length === 0) return null;

              return (
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      padding: { xs: "4px 8px", sm: "6px 12px" },
                      borderRadius: "8px",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                      display: "inline-block",
                      mb: 2,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    {t("admin.employees.stats.noJobTitle", {
                      count: employeesWithoutJob.length,
                    })}
                  </Typography>

                  <TableContainer
                    component={Paper}
                    sx={{
                      backgroundColor: darkMode ? "#333" : "#fff",
                      border: darkMode ? "2px solid " + primaryColor : null,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      borderRadius: 2,
                      mb: 4,
                    }}
                  >
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("user.myTeam.photo")}
                          </TableCell>

                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.name")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.email")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.branch")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.role")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.employmentType")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.directManager")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.employees.employeeDialog.fields.accessRights")}
                          </TableCell>
                        </TableRow>
                        {employeesWithoutJob.map((employee) => (
                          <TableRow
                            key={employee.id}
                            hover
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.1)"
                                    : "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                            onClick={() => handleOpenDialog(employee)}
                          >
                            <TableCell>
                              <EmployeeAvatar photo={employee.photo} />
                            </TableCell>

                            <TruncatedCell value={employee.name} />
                            <TruncatedCell value={employee.email} />
                            <TruncatedCell
                              value={
                                branches.find((b) => b.branchId === employee.branchId)
                                  ?.branchName || "-"
                              }
                            />

                            {/* Role from isAdmin */}
                            <TruncatedCell value={employee.isAdmin ? "Admin" : "User"} />

                            {/* Employment Type Formatting */}
                            <TruncatedCell
                              value={
                                employee.employmentType === "FULL_TIME"
                                  ? "Full Time"
                                  : employee.employmentType === "PART_TIME"
                                    ? "Part Time"
                                    : "-"
                              }
                            />

                            {/* Manager Name from managerId */}
                            <TruncatedCell
                              value={
                                allEmps?.find((e) => e.id === employee.directManagerId)?.name || "-"
                              }
                            />
                            <TableCell>
                              {employee.isAdmin && (
                                <Button
                                  size="small"
                                  disabled={!canEdit}
                                  onClick={(e) => {
                                    e.stopPropagation(); // prevent triggering row click

                                    setEmployeeAccessRightsId(employee.id);
                                    setEmployeeAccessRightsName(employee.name);
                                    setAccessOpen(true);
                                  }}
                                >
                                  {t("admin.employees.employeeDialog.fields.accessRights")}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress size={30} />
        </Box>
      )}

      {canEdit && (
        <MDBox sx={{ position: "fixed", bottom: 20, right: 20 }}>
          <Fab color="primary" aria-label="add" onClick={() => handleOpenDialog(null)}>
            <Icon>add</Icon>
          </Fab>
        </MDBox>
      )}

      <AddEmployeeDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedEmployee(null);
        }}
        jobs={jobs}
        employees={allEmps}
        onSave={handleSave}
        employee={selectedEmployee}
        branches={branches}
        onDelete={handleDelete}
        departments={departments}
      />
      {/* Snackbar for Notifications */}

      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message={`Are you sure you want to delete ${toDeleteEmployees.length} employee(s)?`}
        onConfirm={async () => {
          setLoadingDelete(true); // start loading AFTER confirm
          try {
            await handleDeleteSelectedEmployees();
            setConfOpen(false);
          } catch (err) {
            snackbarRef.current?.showSnackbar(
              t("admin.employees.singleEmployeeDeletedSuccess"),
              "error"
            );
          } finally {
            setLoadingDelete(false);
          }
        }}
      />

      <AccessRightsDialog
        open={accessOpen}
        onClose={() => {
          setAccessOpen(false);
        }}
        employeeId={employeeAccessRightsId}
        employeeName={employeeAccessRightsName}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

EmployeesView.propTypes = {
  jobs: PropTypes.array.isRequired,
  branches: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default EmployeesView;
